import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { EntityService } from '../../../core/entity/entity.service';
import { InvitationService } from '../../../core/entity/invitation.service';
import {
  EntityMember,
  Invitation,
  InvitationsResponse,
} from '../../../core/entity/invitation.types';
import { EntityDetail } from '../../../core/entity/entity.types';
import { InviteDialogComponent } from '../invite-dialog/invite-dialog';
import { IconComponent } from '../../../shared/icon/icon';

/**
 * The dedicated "Team & invitations" page for a business. Lists active members
 * and pending invitations, and lets an entity-admin (or the linked agent) invite
 * people and cancel outstanding invites. Reached from the dashboard's actions
 * menu ("Team"). Only invite-capable roles land here with a populated view; a
 * view-only member sees the roster read-only.
 */
@Component({
  selector: 'app-entity-team',
  imports: [MatCardModule, MatButtonModule, RouterLink, DatePipe, IconComponent],
  templateUrl: './entity-team.html',
  styleUrl: './entity-team.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityTeamComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly invitationService = inject(InvitationService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  protected readonly members = signal<EntityMember[]>([]);
  protected readonly invitations = signal<Invitation[]>([]);
  protected readonly pendingInvitations = computed(() =>
    this.invitations().filter((i) => i.status === 'PENDING' && !i.is_expired),
  );

  private readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';

  /** Entity-admins and the linked agent may invite people to the company. */
  protected canInvite(entity: EntityDetail | null): boolean {
    return entity?.your_role === 'entity-admin' || entity?.your_role === 'agent';
  }

  ngOnInit(): void {
    this.load();
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.cdr.markForCheck();
    this.load();
  }

  private load(): void {
    this.entityService
      .getEntity(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          this.entity.set(entity);
          this.isLoading.set(false);
          this.cdr.markForCheck();
          if (this.canInvite(entity)) {
            this.loadInvitations();
          }
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private loadInvitations(): void {
    this.invitationService
      .list(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.members.set(res.members);
          this.invitations.set(res.invitations);
          this.cdr.markForCheck();
        },
        // Non-fatal: the page still works without the invitations list.
        error: () => undefined,
      });
  }

  protected openInviteDialog(entity: EntityDetail): void {
    this.dialog
      .open(InviteDialogComponent, {
        data: { shortId: entity.short_id, entityName: entity.name },
        width: '480px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res?: InvitationsResponse) => {
        if (res) {
          this.members.set(res.members);
          this.invitations.set(res.invitations);
          this.snackBar.open('Invitation sent.', 'Dismiss', { duration: 4000 });
          this.cdr.markForCheck();
        }
      });
  }

  protected cancelInvitation(inviteId: string): void {
    this.invitationService
      .cancel(this.entityId, inviteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.invitations.update((list) =>
            list.map((i) => (i.id === inviteId ? { ...i, status: 'CANCELLED' as const } : i)),
          );
          this.snackBar.open('Invitation cancelled.', 'Dismiss', { duration: 4000 });
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open('Could not cancel the invitation.', 'Dismiss', {
            duration: 4000,
          });
        },
      });
  }
}
